import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Instagram, Mail, MapPin, Phone } from 'lucide-react';
import api from '@/lib/api';
import useContactSettings from '@/hooks/useContactSettings';

export default function Footer() {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const contacts = useContactSettings();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post('/newsletter/subscribe', { email, language: i18n.language });
      toast.success(i18n.language === 'it' ? 'Iscrizione confermata.' : 'Subscription confirmed.');
      setEmail('');
    } catch {
      toast.error(t('common.error'));
    } finally { setSubmitting(false); }
  };

  return (
    <footer className="bg-[#0F3530] text-white" data-testid="footer">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-28">
        {/* Newsletter */}
        <div className="grid md:grid-cols-2 gap-6 md:gap-24 pb-10 md:pb-16 border-b border-[#1A4A43]">
          <div className="min-w-0">
            <div className="overline text-[#C9A961] mb-3 md:mb-4">{t('footer.newsletter')}</div>
            <h3 className="font-serif text-xl md:text-4xl font-light leading-tight">
              {t('footer.newsletter_sub')}
            </h3>
          </div>
          <form onSubmit={subscribe} className="flex min-w-0 items-end" data-testid="newsletter-form">
            <input
              type="email"
              required
              placeholder={t('footer.email_placeholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-email"
              className="min-w-0 flex-1 bg-transparent border-0 border-b border-[#1A4A43] focus:border-[#C9A961] outline-none text-white placeholder-white/40 py-4 px-0 text-sm tracking-wide"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="newsletter-submit"
              className="overline text-[#C9A961] hover:text-white border-b border-[#C9A961] hover:border-white transition-all duration-500 px-4 py-4"
            >
              {t('footer.subscribe')}
            </button>
          </form>
        </div>

        {/* Columns — full version on desktop only */}
        <div className="hidden md:grid md:grid-cols-3 gap-12 py-16">
          <div>
            <div className="font-serif text-2xl mb-2 text-[#C9A961]">Harizi</div>
            <div className="overline text-white/60 mb-6">Gioielli</div>
            <p className="text-sm text-white/70 leading-relaxed font-light max-w-xs">
              {i18n.language === 'it'
                ? 'Maison di alta gioielleria. Pezzi unici, gemme rare, artigianalità tramandata.'
                : 'High jewelry maison. Unique pieces, rare gemstones, heritage craftsmanship.'}
            </p>
          </div>

          <div>
            <div className="overline text-[#C9A961] mb-6">Menu</div>
            <ul className="space-y-3 text-sm text-white/70 font-light">
              <li><Link to="/pronta-consegna" className="hover:text-[#C9A961] transition-colors duration-500">{t('nav.pronta')}</Link></li>
              <li><Link to="/legacy" className="hover:text-[#C9A961] transition-colors duration-500">Legacy</Link></li>
              <li><Link to="/atelier" className="hover:text-[#C9A961] transition-colors duration-500">{t('nav.atelier')}</Link></li>
              <li><Link to="/concierge" className="hover:text-[#C9A961] transition-colors duration-500">{t('nav.contatti')}</Link></li>
            </ul>
          </div>

          <div>
            <div className="overline text-[#C9A961] mb-6">{i18n.language === 'it' ? 'Contatti' : 'Contact'}</div>
            <ul className="space-y-3 text-sm text-white/70 font-light">
              {contacts.publicPhoneDisplay && <li className="flex items-center gap-2" data-testid="footer-contact-phone"><Phone size={13} /> {contacts.publicPhoneDisplay}</li>}
              {contacts.contactEmail && <li className="flex items-center gap-2" data-testid="footer-contact-email"><Mail size={13} /> {contacts.contactEmail}</li>}
              {contacts.address && <li className="flex items-center gap-2" data-testid="footer-contact-address"><MapPin size={13} /> {contacts.address}</li>}
              {contacts.instagramHref && <li className="pt-4">
                <a
                  href={contacts.instagramHref}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 px-5 py-2.5 border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0F3530] transition-all duration-500"
                  aria-label="Instagram Harizi Gioielli"
                  data-testid="social-instagram"
                >
                  <Instagram size={18} />
                  <span className="overline text-[10px]">{contacts.instagramLabel}</span>
                </a>
              </li>}
            </ul>
          </div>
        </div>

        {/* Footer compatto mobile — solo essenziali */}
        <div className="md:hidden py-10 text-center space-y-5" data-testid="footer-mobile">
          <div className="font-serif text-xl text-[#C9A961]">Harizi <span className="text-white/60 text-sm tracking-[0.2em]">GIOIELLI</span></div>
          <div className="text-xs text-white/60 space-y-1">
            {contacts.publicPhoneDisplay && <div className="flex items-center justify-center gap-2" data-testid="footer-mobile-contact-phone"><Phone size={11} /> {contacts.publicPhoneDisplay}</div>}
            {contacts.contactEmail && <div className="flex items-center justify-center gap-2" data-testid="footer-mobile-contact-email"><Mail size={11} /> {contacts.contactEmail}</div>}
          </div>
          {contacts.instagramHref && <div className="flex items-center justify-center">
            <a
              href={contacts.instagramHref}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#C9A961] text-[#C9A961] hover:bg-[#C9A961] hover:text-[#0F3530] transition-all"
              aria-label="Instagram"
              data-testid="social-instagram-mobile"
            >
              <Instagram size={18} />
              <span className="overline text-[10px]">{contacts.instagramLabel}</span>
            </a>
          </div>}
        </div>

        <div className="pt-8 md:pt-10 border-t border-[#1A4A43] flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 text-[10px] md:text-xs text-white/40 tracking-wide text-center md:text-left">
          <div>© 2026 Harizi Gioielli · {t('footer.rights')}</div>
          <div className="flex flex-wrap items-center justify-center md:justify-end gap-4 md:gap-6">
            <a
              href="https://www.iubenda.com/privacy-policy/73020516"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-[#C9A961] transition-colors"
              title="Privacy Policy"
              data-testid="footer-iubenda-privacy-link"
            >
              Privacy Policy
            </a>
            <a
              href="https://www.iubenda.com/privacy-policy/73020516/cookie-policy"
              className="iubenda-white iubenda-noiframe iubenda-embed hover:text-[#C9A961] transition-colors"
              title="Cookie Policy"
              data-testid="footer-iubenda-cookie-link"
            >
              Cookie Policy
            </a>
            <Link to="/termini" className="hover:text-[#C9A961] transition-colors" data-testid="footer-terms-link">
              {i18n.language === 'it' ? 'Termini & Condizioni' : 'Terms & Conditions'}
            </Link>
            <span className="overline text-[#C9A961] hidden md:inline">Jewelry as Legacy</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
